<script lang="ts">
  import { authState, logout } from '../../lib/auth.svelte';
  import { getUserSubscriptions } from '../../lib/stripe';
  import { db } from '../../lib/firebase';
  import { doc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
  import { onMount } from 'svelte';

  // Child components
  import AccountStatus from './AccountStatus.svelte';
  import DashboardSidebar from './DashboardSidebar.svelte';
  import DashboardOverview from './DashboardOverview.svelte';

  // Page components
  import StaffManagement from '../staff/StaffManagement.svelte';
  import BusinessManagement from '../business/BusinessManagement.svelte';
  import BusinessInvitations from '../business/BusinessInvitations.svelte';
  import Integrations from '../integrations/Integrations.svelte';
  import Support from '../support/Support.svelte';
  import Subscription from '../subscription/Subscription.svelte';
  import CreditApplication from '../credit/CreditApplication.svelte';

  // Navigation state
  type NavItem = 'overview' | 'subscription' | 'businesses' | 'team' | 'business-invitations' | 'integrations' | 'credit-application' | 'support';
  let activeNav = $state<NavItem>('overview');

  let subscriptions = $state<any[]>([]);
  let loading = $state(true);
  let error = $state('');

  // Approval status state
  let approvalStatus = $state<'pending' | 'approved' | 'rejected' | null>(null);
  let isActive = $state<boolean | null>(null);
  let loadingApproval = $state(true);

  // Sidebar reference for mobile toggle
  let sidebarRef = $state<DashboardSidebar | null>(null);

  onMount(() => {
    if (authState.user) {
      // Real-time listener for user document (approval status changes)
      const userDocRef = doc(db, 'users', authState.user.uid);
      const unsubscribeUser = onSnapshot(userDocRef, (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          approvalStatus = data.approvalStatus || 'approved';
          isActive = data.isActive !== false;
        }
        loadingApproval = false;
      });

      // Load subscriptions
      getUserSubscriptions(authState.user.uid)
        .then((subs) => {
          subscriptions = subs;
        })
        .catch((err: any) => {
          error = err.message;
        })
        .finally(() => {
          loading = false;
        });

      return () => {
        unsubscribeUser();
      };
    } else {
      loadingApproval = false;
      loading = false;
    }
  });

  async function handleLogout() {
    await logout();
    window.location.hash = 'login';
  }

  // Check if user has outsourced warehouses (for business invitations)
  let hasOutsourcedWarehouses = $state(false);

  async function checkOutsourcedWarehouses() {
    if (!authState.user) return;
    try {
      const warehouseRef = collection(db, 'warehousedata');
      const q = query(
        warehouseRef,
        where('isOutsourced', '==', true),
        where('ownerId', '==', authState.user.uid)
      );
      const snapshot = await getDocs(q);
      hasOutsourcedWarehouses = snapshot.docs.length > 0;
    } catch (error) {
      console.error('Error checking outsourced warehouses:', error);
    }
  }

  $effect(() => {
    if (authState.user && !authState.loading) {
      checkOutsourcedWarehouses();
    }
  });

  // Navigation items
  const navItems = $derived([
    { id: 'overview' as NavItem, label: 'Overview', icon: 'home', show: true },
    { id: 'subscription' as NavItem, label: 'Subscription', icon: 'credit-card', show: true },
    { id: 'businesses' as NavItem, label: 'Businesses', icon: 'building', show: true },
    { id: 'team' as NavItem, label: 'Team', icon: 'users', show: authState.isAccountAdmin || authState.isSystemAdmin },
    { id: 'business-invitations' as NavItem, label: 'Client Invites', icon: 'briefcase', show: (authState.isAccountAdmin || authState.isSystemAdmin) && hasOutsourcedWarehouses },
    { id: 'integrations' as NavItem, label: 'Integrations', icon: 'plug', show: true },
    { id: 'credit-application' as NavItem, label: 'Freight Credit', icon: 'truck', show: authState.isAccountAdmin || authState.isSystemAdmin },
    { id: 'support' as NavItem, label: 'Support', icon: 'help', show: true },
  ].filter(item => item.show));

  // Page title mapping
  const pageTitles: Record<NavItem, string> = {
    'overview': 'Overview',
    'subscription': 'Subscription',
    'businesses': 'Businesses & Warehouses',
    'team': 'Team Management',
    'business-invitations': 'Client Invitations',
    'integrations': 'Integrations',
    'credit-application': 'Freight Credit Application',
    'support': 'Support',
  };

  // Determine account status for AccountStatus component
  function getAccountStatus(): 'loading' | 'pending' | 'rejected' | null {
    if (loadingApproval) return 'loading';
    if (approvalStatus === 'pending' || !isActive) return 'pending';
    if (approvalStatus === 'rejected') return 'rejected';
    return null;
  }
</script>

<div class="dashboard-container">
  {#if getAccountStatus()}
    <AccountStatus status={getAccountStatus()!} onLogout={handleLogout} />
  {:else}
    <!-- Normal dashboard with sidebar -->
    <div class="dashboard-layout">
      <DashboardSidebar
        bind:this={sidebarRef}
        {activeNav}
        {navItems}
        onNavigate={(nav) => activeNav = nav}
        onLogout={handleLogout}
      />

      <!-- Main content area -->
      <main class="main-content">
        <header class="content-header">
          <button class="mobile-menu-btn" onclick={() => sidebarRef?.toggleMobile()}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <h1>{pageTitles[activeNav]}</h1>
        </header>

        <div class="content-body">
          {#if error}
            <div class="error-banner" role="alert">
              {error}
            </div>
          {/if}

          {#if activeNav === 'overview'}
            <DashboardOverview
              {subscriptions}
              {loading}
              onNavigate={(nav) => activeNav = nav as NavItem}
            />
          {:else if activeNav === 'subscription'}
            <Subscription />
          {:else if activeNav === 'businesses'}
            <BusinessManagement />
          {:else if activeNav === 'team'}
            <StaffManagement />
          {:else if activeNav === 'business-invitations'}
            <BusinessInvitations />
          {:else if activeNav === 'integrations'}
            <Integrations />
          {:else if activeNav === 'credit-application'}
            <CreditApplication />
          {:else if activeNav === 'support'}
            <Support />
          {/if}
        </div>
      </main>
    </div>
  {/if}
</div>

<style>
  /* Dashboard Container */
  .dashboard-container {
    min-height: 100vh;
    background: #f5f7fa;
  }

  /* Dashboard Layout with Sidebar */
  .dashboard-layout {
    display: flex;
    min-height: 100vh;
  }

  /* Main Content Area */
  .main-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .content-header {
    background: white;
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .content-header h1 {
    margin: 0;
    font-size: 1.5rem;
    color: #333;
  }

  .content-body {
    flex: 1;
    padding: 2rem;
    overflow-y: auto;
  }

  .error-banner {
    background: #fee;
    border: 1px solid #fcc;
    color: #c33;
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 2rem;
  }

  /* Mobile Menu Button - Hidden on desktop */
  .mobile-menu-btn {
    display: none;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .mobile-menu-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border: none;
      background: #f3f4f6;
      border-radius: 8px;
      cursor: pointer;
      color: #333;
      margin-right: 1rem;
      flex-shrink: 0;
      transition: background 0.2s;
    }

    .mobile-menu-btn:hover {
      background: #e5e7eb;
    }

    .mobile-menu-btn svg {
      width: 24px;
      height: 24px;
    }

    .content-header {
      display: flex;
      align-items: center;
      padding: 1rem;
    }

    .content-header h1 {
      font-size: 1.25rem;
    }

    .content-body {
      padding: 1rem;
    }
  }
</style>
